import React, { useState, useEffect, useMemo } from "react";
import { api } from "../api/client";
import LoadingSpinner from "../components/LoadingSpinner";
import ImageModal from "../components/ImageModal";

// One-time fridge survey helper. Reviewers pick a site, look through every
// recent NVR + PTZ camera angle to find the fridge, then record their
// assessment in a spreadsheet.
//
// Default view is "Freshest (per camera)": each NVR channel shows its most
// recent snapshot, falling back to an older (stale) image if the latest grab
// failed - so a camera angle we have ever captured is never hidden. The date
// dropdown drills into a single capture date.
function FridgeSurvey() {
    const [stores, setStores] = useState([]);
    const [siteId, setSiteId] = useState("");
    const [data, setData] = useState(null);
    const [date, setDate] = useState(""); // "" = freshest/merged; else a YYYY-MM-DD
    const [loading, setLoading] = useState(false);
    const [storesLoading, setStoresLoading] = useState(true);
    const [error, setError] = useState(null);
    const [modalIndex, setModalIndex] = useState(null);

    // Load the site list once
    useEffect(() => {
        api.stores()
            .then((rows) => {
                const sorted = [...rows].sort((a, b) => String(a.siteId).localeCompare(String(b.siteId)));
                setStores(sorted);
            })
            .catch((e) => setError(e.message))
            .finally(() => setStoresLoading(false));
    }, []);

    // Load images whenever the chosen site or date changes
    useEffect(() => {
        if (!siteId) { setData(null); return; }
        setLoading(true);
        setError(null);
        api.fridgeSurveyImages(siteId, date)
            .then((d) => setData(d))
            .catch((e) => { setError(e.message); setData(null); })
            .finally(() => setLoading(false));
    }, [siteId, date]);

    const onPickSite = (e) => {
        setDate("");        // reset to freshest for the new site
        setData(null);
        setSiteId(e.target.value);
    };

    const modalImages = useMemo(() => {
        if (!data) return [];
        return data.cameras.map((c) => ({
            url: c.url,
            filename: c.filename,
            zone: c.label
                + (c.isPtz && c.displayTime ? ` @ ${c.displayTime}` : "")
                + (c.capturedDate ? ` (${c.capturedDate})` : ""),
            capturedAt: c.capturedAt,
        }));
    }, [data]);

    const selectedStore = stores.find((s) => String(s.siteId) === String(siteId));

    return (
        <div>
            <div className="page-header">
                <h2>Fridge Survey</h2>
                <p style={{ color: "var(--text-muted)" }}>
                    Pick a site, look through every camera angle to find the fridge, then record
                    your assessment in the survey spreadsheet. Shows the latest scan framing per
                    preset (green - e.g. FZ=freezer, FW/CF=fridge, GD=gondola) plus NVR and PTZ
                    snapshots. Freshest image per camera/preset; older (stale) ones are kept and
                    badged so nothing is hidden.
                </p>
            </div>

            {/* Controls */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
                <select
                    value={siteId}
                    onChange={onPickSite}
                    className="filter-select"
                    style={{ fontSize: 14, padding: "8px 12px", minWidth: 280 }}
                    disabled={storesLoading}
                >
                    <option value="">{storesLoading ? "Loading sites..." : "Select a site..."}</option>
                    {stores.map((s) => (
                        <option key={s.siteId} value={s.siteId}>
                            {s.siteId} {"—"} {s.name}{s.stateCode ? ` (${s.stateCode})` : ""}
                        </option>
                    ))}
                </select>

                {data && data.availableDates && data.availableDates.length > 0 && (
                    <select
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        className="filter-select"
                        style={{ fontSize: 14, padding: "8px 12px" }}
                    >
                        <option value="">Freshest (per camera)</option>
                        {data.availableDates.map((d) => (
                            <option key={d} value={d}>{d}</option>
                        ))}
                    </select>
                )}

                {data && !loading && (
                    <span style={{ color: "var(--text-muted)", fontSize: 13 }}>
                        {data.totalImages} images
                        {data.presetCount > 0 && ` (${data.presetCount} preset framings)`}
                        {data.staleCount > 0 && ` - ${data.staleCount} stale`}
                        {data.isPreview && " - latest preview, no dated captures found"}
                    </span>
                )}
            </div>

            {error && (
                <div className="card" style={{ padding: 20, color: "#dc2626" }}>
                    Failed to load images: {error}
                </div>
            )}

            {!siteId && !error && (
                <div className="card" style={{ padding: 40, textAlign: "center" }}>
                    <h3 style={{ color: "var(--text-secondary)" }}>Select a site to begin</h3>
                    <p style={{ color: "var(--text-muted)" }}>
                        All NVR channels and the PTZ camera for the site will be shown.
                    </p>
                </div>
            )}

            {loading && <LoadingSpinner message="Loading images..." />}

            {!loading && siteId && data && data.cameras.length === 0 && (
                <div className="card" style={{ padding: 40, textAlign: "center" }}>
                    <h3 style={{ color: "var(--text-secondary)" }}>No images available</h3>
                    <p style={{ color: "var(--text-muted)" }}>
                        No recent snapshots found for site {siteId}.
                    </p>
                </div>
            )}

            {!loading && data && data.cameras.length > 0 && (
                <>
                    {selectedStore && (
                        <h3 style={{ marginBottom: 12 }}>
                            {selectedStore.siteId} {"—"} {selectedStore.name}
                        </h3>
                    )}
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
                        {data.cameras.map((cam, i) => (
                            <div
                                key={`${cam.kind}-${cam.camera}-${cam.capturedDate || ""}-${cam.time || i}`}
                                onClick={() => setModalIndex(i)}
                                style={{
                                    cursor: "pointer", position: "relative",
                                    borderRadius: 8, overflow: "hidden",
                                    border: cam.kind === "preset" ? "2px solid #16a34a"
                                        : cam.kind === "ptz" ? "2px solid var(--primary)"
                                        : "1px solid var(--border)",
                                }}
                            >
                                <img
                                    src={cam.url}
                                    alt={cam.label}
                                    style={{ width: 300, height: 200, objectFit: "cover", display: "block" }}
                                    loading="lazy"
                                />
                                {cam.stale && cam.capturedDate && (
                                    <div style={staleBadge} title={`Stale - captured ${cam.capturedDate}`}>
                                        {cam.capturedDate}
                                    </div>
                                )}
                                <div style={tileLabel}>
                                    {cam.label}
                                    {cam.isPtz && cam.displayTime ? ` - ${cam.displayTime}` : ""}
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}

            {modalIndex !== null && modalImages.length > 0 && (
                <ImageModal
                    images={modalImages}
                    currentIndex={modalIndex}
                    onClose={() => setModalIndex(null)}
                    onPrev={modalIndex > 0 ? () => setModalIndex(modalIndex - 1) : null}
                    onNext={modalIndex < modalImages.length - 1 ? () => setModalIndex(modalIndex + 1) : null}
                />
            )}
        </div>
    );
}

const tileLabel = {
    position: "absolute", bottom: 0, left: 0, right: 0,
    background: "rgba(0,0,0,0.7)", padding: "5px 10px",
    fontSize: 13, color: "#fff", fontWeight: 600,
};

const staleBadge = {
    position: "absolute", top: 6, right: 6,
    background: "rgba(217,119,6,0.92)", color: "#fff",
    padding: "2px 7px", borderRadius: 4, fontSize: 11, fontWeight: 700,
};

export default FridgeSurvey;
